import type { ReactNode } from 'react';

interface PageHeaderProps {
  title: string;
  description?: string;
  actions?: ReactNode;
  badge?: ReactNode;
}

// Sticky page-level header. Bleeds out via negative margins so it visually
// spans full width even when the page wraps its content in max-w-3xl /
// max-w-screen-md etc. Stays under the global admin top bar (which sits at
// top:0 z:30) by ranking z:20 here.
//
// Applied globally — every page that renders <PageHeader> automatically
// gets sticky title + sticky action buttons.
export function PageHeader({ title, description, actions, badge }: PageHeaderProps) {
  return (
    <div
      className={[
        'sticky top-0 z-20',
        'bg-slate-50/95 backdrop-blur-sm',
        '-mx-4 sm:-mx-6 lg:-mx-8 -mt-6 lg:-mt-8',
        'px-4 sm:px-6 lg:px-8 py-3.5 lg:py-4',
        'mb-5 lg:mb-6',
        'border-b border-slate-200',
        'flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3',
      ].join(' ')}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2.5">
          <h1 className="text-xl lg:text-2xl font-bold text-slate-900 tracking-tight truncate">{title}</h1>
          {badge}
        </div>
        {description && <p className="text-xs lg:text-sm text-slate-500 mt-0.5 max-w-2xl truncate sm:whitespace-normal">{description}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 flex-shrink-0">{actions}</div>}
    </div>
  );
}
